import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';

const AMOUNT_OPTIONS = ['1g', '2g', '3.5g', '7g', '14g'];

export default function ManualOrderPanel() {
  const showToast = useToast();
  const [strains, setStrains] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [form, setForm] = useState({ customer_name: '', strain_id: '', amount: '', custom_price: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = () => {
    api.get('/admin/strains/all').then(s => setStrains(s.filter(st => st.is_in_stock))).catch(() => {});
    api.get('/admin/sales/manual').then(setRecentOrders).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.custom_price === '') delete payload.custom_price;
      else payload.custom_price = parseFloat(payload.custom_price);
      await api.post('/bookings/manual', payload);
      showToast('success', `Order recorded for ${form.customer_name}`);
      setForm({ customer_name: '', strain_id: '', amount: '', custom_price: '' });
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>Manual Order</h3>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        Record a sale for walk-in customers not on the app
      </p>

      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 8 }}>
          <input
            placeholder="Customer name"
            value={form.customer_name}
            onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
            required
          />
          <select
            value={form.strain_id}
            onChange={e => setForm(f => ({ ...f, strain_id: e.target.value }))}
            required
          >
            <option value="">Select strain...</option>
            {strains.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({parseFloat(s.stock_grams)}g)</option>
            ))}
          </select>
          <select
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            required
          >
            <option value="">Amount...</option>
            {AMOUNT_OPTIONS.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Custom price £ (optional)"
            value={form.custom_price}
            onChange={e => setForm(f => ({ ...f, custom_price: e.target.value }))}
          />
        </div>
        {form.custom_price !== '' && (
          <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>
            Discounted price set — standard rate will not apply
          </div>
        )}
        <button type="submit" className="btn-primary btn-small" disabled={saving}>
          {saving ? 'Recording...' : 'Record Sale'}
        </button>
      </form>

      {/* Recent Manual Orders */}
      {recentOrders.length > 0 && (
        <div style={{ maxHeight: 250, overflowY: 'auto' }}>
          <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Recent Manual Sales</h4>
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Strain</th>
                <th>Amount</th>
                <th>Sale</th>
                <th>Profit</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(o => (
                <tr key={o.id}>
                  <td><strong>{o.manual_customer}</strong></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{o.strain_name || '-'}</td>
                  <td>{parseFloat(o.amount)}g</td>
                  <td>{parseFloat(o.price_charged).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td style={{ color: 'var(--success)' }}>{parseFloat(o.profit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(o.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
